import { useEffect, useState } from "react"

import {
  Box,
  Flex,
  Heading,
  Spacer,
  VStack,
  Image,
  Text,
  Button,
  useToast,
} from "@chakra-ui/react"

import { Table, Space } from "antd"
import type {
  ColumnsType,
  TablePaginationConfig,
} from "antd/lib/table/interface"
import { RiDownload2Line } from "react-icons/ri"
import axios from "axios"

import DatePicker from "react-datepicker"
import "react-datepicker/dist/react-datepicker.css"

interface Report {
  _id: string
  title: string
  description?: string
  downloadUrl: string
  metadata?: {
    size?: number
    mimeType?: string
  }
  createdAt: string
}

interface ApiResponse {
  data: Report[]
  total: number
}

// helper to format date as yyyy-mm-dd (local time)
const formatDate = (d: Date) => {
  const year = d.getFullYear()
  const month = String(d.getMonth() + 1).padStart(2, "0")
  const day = String(d.getDate()).padStart(2, "0")
  return `${year}-${month}-${day}`
}

const ReportsTable: React.FC = () => {
  const [data, setData] = useState<Report[]>([])
  const [loading, setLoading] = useState<boolean>(false)
  const toast = useToast()

  const [pagination, setPagination] = useState<TablePaginationConfig>({
    current: 1,
    pageSize: 10,
    total: 0,
  })

  // date range for generating report
  const [startDate, setStartDate] = useState<Date | null>(null)
  const [endDate, setEndDate] = useState<Date | null>(null)

  const fetchReports = async (signal?: AbortSignal) => {
    try {
      setLoading(true)

      const page = pagination.current || 1
      const limit = pagination.pageSize || 10

      const params = new URLSearchParams({
        page: String(page),
        limit: String(limit),
      })

      const res = await axios({
        method: "GET",
        url: `${
          process.env.REACT_APP_BASE_URL
        }/admin/report?${params.toString()}`,
        withCredentials: true,
        signal,
      })

      const json: ApiResponse = res.data

      setData(json.data || [])
      setPagination((prev) => ({
        ...prev,
        total: json.total || 0,
      }))
    } catch (err: any) {
      if (err.name !== "AbortError") console.error(err)
    } finally {
      setLoading(false)
    }
  }

  // Fetch reports with pagination
  useEffect(() => {
    const controller = new AbortController()
    fetchReports(controller.signal)
    return () => controller.abort()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination.current, pagination.pageSize])

  const handleTableChange = (newPagination: TablePaginationConfig) => {
    setPagination((prev) => ({
      ...prev,
      current: newPagination.current || 1,
      pageSize: newPagination.pageSize || prev.pageSize,
    }))
  }

  const handleDownload = (record: Report) => {
    window.open(record.downloadUrl, "_blank")
  }

  const handleGenerateReport = async () => {
    if (!startDate || !endDate) {
      alert("Please select both start and end dates.")
      return
    }

    if (endDate < startDate) {
      alert("End date cannot be before start date.")
      return
    }

    const payload = {
      startDate: formatDate(startDate), // yyyy-mm-dd
      endDate: formatDate(endDate), // yyyy-mm-dd
    }

    try {
      setLoading(true)

      // 🔁 change URL to your real generate endpoint
      await axios({
        method: "POST",
        url: `${process.env.REACT_APP_BASE_URL}/admin/report/generate`,
        withCredentials: true,
        data: payload,
      })

      toast({
        title: "Success",
        description: "Report will be seen once its available.",
        status: "success",
        duration: 3000,
        isClosable: true,
        position: "top-right",
      })
    } catch (err) {
      toast({
        title: "Error",
        description: "An error occurred while generating report.",
        status: "error",
        duration: 3000,
        isClosable: true,
      })
      console.error("Error generating report:", err)
    } finally {
      setLoading(false)
    }
  }

  const columns: ColumnsType<Report> = [
    {
      title: "Nos.",
      width: 60,
      render: (_: any, __: any, index: number) => `#${index + 1}`,
    },
    {
      title: "Title",
      dataIndex: "title",
      width: 250,
    },
    {
      title: "Description",
      dataIndex: "description",
      ellipsis: true,
    },
    {
      title: "Size",
      dataIndex: ["metadata", "size"],
      width: 120,
      render: (size) => (size ? `${(size / 1024).toFixed(1)} KB` : "-"),
    },
    {
      title: "Created",
      dataIndex: "createdAt",
      width: 180,
      render: (date) => new Date(date).toLocaleString(),
    },
    {
      title: "Action",
      width: 100,
      render: (_: any, record: Report) => (
        <Space>
          <RiDownload2Line
            style={{
              color: "#212121",
              cursor: "pointer",
              height: "20px",
              width: "20px",
            }}
            onClick={() => handleDownload(record)}
          />
        </Space>
      ),
    },
  ]

  return (
    <div
      style={{
        width: "96vw",
        maxWidth: "96vw",
        maxHeight: "86vh",
        height: "86vh",
        overflowY: "auto",
        backgroundColor: "#EEEEEE",
        padding: "30px",
      }}
    >
      <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
        {/* Date range + Generate button */}
        <Flex mb={4} align="center" gap={4}>
          <Box>
            <Text fontSize="xs" mb={1}>
              Select Date Range
            </Text>

            <DatePicker
              selectsRange
              startDate={startDate}
              endDate={endDate}
              onChange={(dates) => {
                const [start, end] = dates as [Date | null, Date | null]
                setStartDate(start)
                setEndDate(end)
              }}
              dateFormat="yyyy-MM-dd"
              placeholderText="Select date range"
              isClearable
            />
          </Box>

          <Button
            onClick={handleGenerateReport}
            isLoading={loading}
            bg="#E24441"
            _hover={{ bg: "#d73c39" }}
            color="white"
            borderRadius="8px"
            fontSize="sm"
          >
            Generate Report
          </Button>
        </Flex>

        <Table<Report>
          rowKey="_id"
          columns={columns}
          dataSource={data}
          loading={loading}
          pagination={{
            ...pagination,
            position: ["bottomRight"],
          }}
          scroll={{ x: "max-content" }}
          onChange={handleTableChange}
          style={{ backgroundColor: "white", borderRadius: 6 }}
        />
      </div>
    </div>
  )
}

const Reports = () => {
  return (
    <>
      <VStack top={5} position="relative" key={"vStack"}>
        <Flex w="92%" alignItems="center" justifyContent="space-between">
          <Heading ml="8" size="md" fontWeight="semibold" color="black.400">
            Reports Management
          </Heading>
          <Spacer></Spacer>
          <Heading size="sm" fontWeight="semibold">
            <Flex alignItems="center" gap={2}>
              <Image
                borderRadius="full"
                boxSize="50px"
                src="https://bit.ly/dan-abramov"
                alt="Dan Abramov"
              />
              <Text>Admin</Text>
            </Flex>
          </Heading>
        </Flex>
      </VStack>
      <ReportsTable />
    </>
  )
}

export default Reports
